/**
 * The agent setup page's words: the server's verdicts (state, health, problems) said plainly. Nothing here works a
 * status out; it only names the one the server sent.
 */
import type { AgentProblem, AgentSetup, SetupSaveResult } from '../api/controlBoard'
import type { StateLabel } from './agents'

/** "just now", "5 minutes ago", "3 hours ago", "2 days ago". */
export function ago(ms: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - ms) / 1000))
  if (s < 60) return 'just now'
  const unit = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'} ago`
  if (s < 3600) return unit(Math.floor(s / 60), 'minute')
  if (s < 86_400) return unit(Math.floor(s / 3600), 'hour')
  return unit(Math.floor(s / 86_400), 'day')
}

/**
 * What an agent's card on the Agents list invites: "Set up" only while setting it up is unfinished -- no connection to
 * Rithmo yet, a hosted sign-in not yet completed, or never heard from yet; otherwise its details (a connection ended later is shown by the
 * card's state, not by asking to set it up again).
 */
/** The tooltip on a description Rithmo wrote (agentDescriptions). */
export const RITHMO_WROTE = 'Written by Rithmo from the agent’s instructions'

export function cardAction(s: Pick<AgentSetup, 'status'>): 'Set up' | 'View details' {
  // Never heard from yet (its credential issued, never used) is unfinished too: it reads Not connected (agentConnectionWords).
  const unfinished = s.status.problems.some((p) => (p.code === 'not_connected' && ['no_identity', 'missing', 'authorization_required'].includes(p.detail))
    || p.code === 'never_checked_in')
  return unfinished ? 'Set up' : 'View details'
}

/**
 * Text as it was typed. Rithmo's API stores what it is sent with & < > " as entities (its input sanitizer), so a purpose
 * saved as `Answers "billing" questions` comes back as `Answers &quot;billing&quot; questions`. This is the exact inverse,
 * once: React still escapes what it renders, so nothing here becomes markup.
 */
export const shownText = (s: string): string =>
  s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&amp;/g, '&')

const HOST_PLATFORM: Record<string, string> = { claude_code: 'Claude Code', openai: 'OpenAI', langchain: 'LangChain' }
const DECLARED_PLATFORM: Record<string, string> = { claude: 'Claude', chatgpt: 'ChatGPT' }

/**
 * What the agent is built on, in one word or two: "Claude Code", "Claude", "ChatGPT", "LangGraph", "Rithmo" or "Custom"
 * (Tim, 2026-10-27: "if it's a custom agent, just say custom"). The most specific thing Rithmo knows wins: what its host
 * reported (Agent Baseline), then the hosted platform it signed in from, then the framework or platform it was added
 * with; an agent Rithmo runs is "Rithmo"; anything else built outside a hosted assistant is "Custom". The agent page and
 * the Agents list both name it here, from the same reads.
 */
export function platformName(s: Pick<AgentSetup, 'agent' | 'connection'>, hostKind?: string | null): string {
  if (hostKind && HOST_PLATFORM[hostKind]) return HOST_PLATFORM[hostKind]
  const c = s.connection
  if (c?.kind === 'registered') return 'Rithmo'
  if (c?.kind === 'oauth' && c.platformClient) {
    let host = ''
    try { host = new URL(c.platformClient).hostname } catch { host = '' }
    if (/(^|\.)claude\.ai$|(^|\.)anthropic\.com$/.test(host)) return 'Claude'
    if (/(^|\.)chatgpt\.com$|(^|\.)openai\.com$/.test(host)) return 'ChatGPT'
  }
  const declared = s.agent.platform
  if (declared?.framework) return declared.framework
  if (declared?.platform && DECLARED_PLATFORM[declared.platform]) return DECLARED_PLATFORM[declared.platform]
  return 'Custom'
}

/** The problems that make an agent Not working (server agentNotWorking). Shown as one status, never as a line of text. */
export const NOT_WORKING_CODES: ReadonlyArray<AgentProblem['code']> = ['runs_without_result', 'credential_rejected']

/** One Not working problem as a tooltip line. */
function notWorkingLine(p: Extract<AgentProblem, { code: 'runs_without_result' | 'credential_rejected' }>, now: number): string {
  if (p.code === 'runs_without_result') return `Last ${p.count} runs ended without a result · from agent’s reports`
  return `Credential refused ${p.count} time${p.count === 1 ? '' : 's'} · last ${ago(p.lastAtMs, now)}`
}

/** "Not working", with why as its tooltip; null when it is working. */
export function notWorkingState(problems: readonly AgentProblem[], now = Date.now()): StateLabel | null {
  const lines = problems.flatMap((p) => (p.code === 'runs_without_result' || p.code === 'credential_rejected' ? [notWorkingLine(p, now)] : []))
  // Amber, so it never makes a second red beside Blocked (Tim, 2026-11-03: "I don't want two red badges").
  return lines.length ? { label: 'Not working', tone: 'warn', detail: lines.join('\n') } : null
}

export function problemText(p: AgentProblem): string {
  switch (p.code) {
    case 'not_connected': return p.detail === 'retired' ? 'Its connection was retired: its credentials no longer work.' : p.detail === 'revoked' ? 'Its connection to Rithmo was revoked.' : p.detail === 'expired' ? 'Its connection to Rithmo has expired.'
      : p.detail === 'authorization_required' ? 'No one has authorized it yet: someone must sign in to Rithmo from the assistant and allow it.' : 'It has no connection to Rithmo.'
    case 'never_checked_in': return 'It hasn’t checked in with Rithmo yet.'
    case 'access_mismatch': return `Its real access to ${p.resource} doesn’t match this setup.`
    case 'owner_not_in_chart': return 'Its owner isn’t in the org chart.'
    case 'no_owner': return 'It has no owner.'
    case 'runs_without_result': case 'credential_rejected': return notWorkingLine(p, Date.now())
  }
}

export const OUTCOME: Record<string, { label: string; tone: StateLabel['tone'] }> = {
  // Rithmo's answer to the check, never whether the agent then acted: that is only an outcome the agent reports.
  went_ahead: { label: 'Allowed by Rithmo', tone: 'ok' },
  held: { label: 'Held for a person', tone: 'warn' },
  refused: { label: 'Denied by Rithmo', tone: 'alert' },
}

const REFUSAL: Record<string, string> = {
  owner_unknown: 'That person isn’t in the org.',
  owner_ambiguous: 'That person matches more than one person in the org.',
  scope_unknown: 'That document is no longer verified.',
  authority_state: 'This organization’s record can’t be changed right now.',
  operator_not_authorized: 'Only an organization admin can change this.',
}

/** What was wrong with a change the server would not take. */
const PROBLEM: Record<string, string> = {
  instructions_invalid: 'The instructions are too long.',
  display_name_invalid: 'The name is too long: 120 characters at most.',
  display_name_required: 'An agent needs a name.',
  display_name_unsupported: 'This agent shares its sign-in with other agents, so its name can’t be changed here.',
  purpose_invalid: 'The description is too long: 500 characters at most.',
}

/** Why a save did not take effect, in one sentence. */
export function whyNotSaved(r: Exclude<SetupSaveResult, { status: 'saved' | 'no_changes' }>): string {
  if (r.status === 'invalid') return PROBLEM[r.problem] ?? 'That change couldn’t be saved.'
  return REFUSAL[r.refusals[0]?.code ?? ''] ?? 'Rithmo refused that change.'
}

/** Why Rithmo would hold the agent on a part of the record (the guard's hold code or status), in words. */
const HELD_WHY: Record<string, string> = {
  contested: 'someone has proposed changing it',
  unresolved_decision: 'a decided change to it is waiting on an answer',
  source_revised: 'the document was edited and no longer says this',
  source_amended: 'the document was edited',
  unsupported_delivery: 'it was carried out without a decision behind it',
  silent_past_cadence: 'no one has confirmed it recently',
  record_changed: 'it changed while the agent was checking',
  stale: 'no one has confirmed it recently',
  out_of_sync: 'it has been replaced',
  no_decision: 'the document does not say',
  not_observed: 'Rithmo cannot see where this is decided',
  record_unavailable: 'Rithmo could not read the document',
}
export const heldWhy = (code: string | null | undefined): string => (code ? HELD_WHY[code] ?? 'Rithmo could not confirm it' : 'Rithmo could not confirm it')

/** Why Rithmo would not go live yet (the go-live refusal's first blocker), in words. */
const LANE_NAME: Record<string, string> = { slack: 'Slack', drive: 'Google Drive', meetings: 'Meetings', github: 'GitHub', structured: 'Data import' }
export function notReadyWhy(blockers: string[]): string {
  const [code, lane] = (blockers[0] ?? '').split(':')
  const name = LANE_NAME[lane] ?? lane
  switch (code) {
    case 'lane_not_operational': return `${name} isn’t syncing yet.`
    case 'undeclared_live_source': return `${name} is connected but not set up.`
    case 'legacy_lane': return `${name} isn’t set up.`
    case 'no_declared_lane': return 'No source is set up.'
    case 'not_preparing': return 'Already live or paused.'
    default: return 'Not ready yet.'
  }
}
